import { createEffect, createSignal, onCleanup, onMount } from "solid-js";
import { Outlet, useLocation, useRouterState } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";

import { RouteTransition } from "@/components/ui/route-transition";

import { appState, useInteractionFlag } from "@fleet/ui";
import { useTheme } from "@/providers/theme-provider";
import { useI18n } from "@/providers/i18n-provider";
import { useSession } from "@/lib/auth";
import { useCommandHandler } from "@fleet/shortcuts";
import { COMMAND_IDS } from "@/lib/shortcuts/defaults";
import { resetUndoScope, undoLastAction } from "@/lib/undo";

import { AppBackground } from "@/components/app-background";
import { AppHeader } from "@/components/app-header";
import { OfflineBanner } from "@/components/offline-banner";

import { AppLayoutSidebar } from "./app-layout-sidebar";

function useGlobalEventListeners(
  isSidebarOpen: () => boolean,
  setIsSidebarOpen: (open: boolean) => void,
) {
  onMount(() => {
    const handleEscapeForSidebar = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSidebarOpen()) {
        setIsSidebarOpen(false);
      }
    };

    globalThis.addEventListener("keydown", handleEscapeForSidebar);

    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleEscapeForSidebar);
    });
  });
}

/**
 * Main GymLog layout: navigation sidebar + header + Outlet.
 * (The lists/todos layout from template was removed: GymLog does not drag
 * dnd, search palette, or task detail drawer.)
 */
export function AppLayoutContent() {
  onMount(() => {
    // Notify the InitialSyncOverlay that we are mounted and ready
    appState.markAsReady();
  });

  const routerState = useRouterState();
  const isNavigating = () => routerState().status === "pending";
  const location = useLocation();

  const theme = useTheme();
  const { t } = useI18n();
  const [isSidebarOpen, setIsSidebarOpen] = createSignal(false);

  // While the mobile drawer is open it owns horizontal swiping (drag-to-
  // dismiss); page-level swipe gestures must stand down while it is.
  useInteractionFlag({ "nav-drawer.open": isSidebarOpen });

  useGlobalEventListeners(isSidebarOpen, setIsSidebarOpen);

  // ── Undo (L1) ──────────────────────────────────────────────────────────
  // Ctrl/Cmd+Z outside inputs/dialogs (`when` lives in defaults.ts). The
  // stack is per-account: when changing active account (multi-account/login/logout)
  // it is emptied — an inverse MUST NEVER execute against another session.
  const session = useSession();
  createEffect(() => {
    resetUndoScope(session.userId());
  });
  useCommandHandler(COMMAND_IDS.UNDO, () => {
    void undoLastAction();
  });

  return (
    <AppBackground
      class="flex h-dvh text-foreground overflow-hidden"
      isBlur={theme.isBlur()}
      background={theme.tokens()?.background}
      patternType={theme.tokens()?.patternType}
      patternOpacity={theme.tokens()?.patternOpacity}
    >
      {/* Skip-to-content (a11y): hidden until receiving focus via Tab. */}
      <a
        href="#main-content"
        class="sr-only focus-visible:not-sr-only focus-visible:absolute focus-visible:left-4 focus-visible:top-4 focus-visible:z-50 focus-visible:rounded-lg focus-visible:bg-primary focus-visible:px-4 focus-visible:py-2 focus-visible:text-sm focus-visible:font-semibold focus-visible:text-primary-foreground focus-visible:shadow-lg"
      >
        {t(msg`Skip to content`)}
      </a>

      <AppLayoutSidebar isOpen={isSidebarOpen} setOpen={setIsSidebarOpen} />

      {/* ── Main content ───────────────────────────────────────── */}
      <main
        id="main-content"
        tabindex="-1"
        class="flex flex-1 flex-col overflow-hidden bg-transparent pt-[var(--safe-area-inset-top)] pr-[var(--safe-area-inset-right)] pb-[var(--safe-area-inset-bottom)]"
      >
        <AppHeader onMenuClick={() => setIsSidebarOpen(true)} isNavigating={isNavigating} />
        <OfflineBanner />
        <div class="flex flex-1 flex-col min-h-0 overflow-hidden pr-[var(--safe-area-inset-right)] pl-[var(--safe-area-inset-left)]">
          {/*
            Entrance animation for every in-app route (canon: profile page). The
            wrapper re-keys on pathname (NOT search params) so each navigation
            re-fires a GPU-only slide-up + fade without remounting on
            query-string changes. Layout classes are preserved verbatim so there
            is zero layout shift.
          */}
          <RouteTransition
            routeKey={location().pathname}
            class="flex flex-1 flex-col min-h-0 h-full"
          >
            <Outlet />
          </RouteTransition>
        </div>
      </main>
    </AppBackground>
  );
}
